/**
 * Reading a paged list whole, a page at a time (known gaps 35 and 108): the scope filters' candidate list and
 * the points system builders' event list read the room's tournament list this way. Imports nothing, so
 * `node --test` drives it directly.
 */

/** Every page read, or as many as could be: `complete` is false when the list is short of `total`. */
export interface AllPages<R> {
  rows: R[];
  total: number;
  complete: boolean;
}

/**
 * The most pages one read takes (10,000 tournaments at the server's 200 a page). A guard against a loop that
 * never ends, not a cap anybody should meet: past it the list says it is incomplete.
 */
export const MAX_PAGES_READ = 50;

/**
 * Reads page after page, one at a time, until it holds `total`.
 *
 * Offset paging over the room list is safe because its date order ends with the tournament's id, a total order
 * (`TournamentRoomListTests`). What it cannot survive is the set changing between pages, so it stops at a page
 * shorter than a full one (or an empty one) whatever `total` says, holds a row repeated across pages once, and
 * reports itself incomplete when it ends short of the last `total` it was told. A failed first page fails the
 * read; a failed later page keeps what was read and says so.
 *
 * **A superseded read stops.** `signal` is the query's: TanStack aborts it when the read stops being wanted (a
 * scope edit moves the key). It is checked between pages, so the page in flight finishes — the HTTP client takes
 * no signal — and no further page is asked for; the read then rejects with an `AbortError`, never answering a
 * partial list as anything at all.
 */
export async function fetchAllPages<R extends { id: string }>(
  fetchPage: (page: number, pageSize: number) => Promise<{ data: R[]; total: number }>,
  pageSize: number,
  maxPages: number = MAX_PAGES_READ,
  signal?: AbortSignal,
): Promise<AllPages<R>> {
  const first = await fetchPage(1, pageSize);
  const seen = new Set<string>();
  const rows: R[] = [];
  const take = (data: R[]) => {
    for (const r of data) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      rows.push(r);
    }
  };

  take(first.data);
  let total = first.total;
  let last = first.data.length;
  let page = 1;

  while (rows.length < total && last === pageSize && page < maxPages) {
    signal?.throwIfAborted();
    page += 1;
    let next: { data: R[]; total: number };
    try {
      next = await fetchPage(page, pageSize);
    } catch {
      // An abort is the read being superseded, never a failed page to report as a short list.
      signal?.throwIfAborted();
      return { rows, total, complete: false };
    }
    take(next.data);
    total = next.total;
    last = next.data.length;
  }

  return { rows, total, complete: rows.length >= total };
}
